import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/src/lib/supabase';
import { log } from '@/src/lib/logger';

// Storage-safe file name that still reads naturally. Supabase storage keys allow letters,
// numbers, spaces and a few symbols, but not accented letters, so Turkish/French characters
// are swapped for plain ones (Çılbır -> Cilbir) and anything else unsafe becomes "-".
export function storageSafeName(name: string): string {
  const plain = name
    .replace(/ı/g, 'i').replace(/İ/g, 'I')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');   // ç->c, ş->s, ğ->g, é->e ...
  return plain
    .replace(/[^a-zA-Z0-9 ._()&+,'!-]/g, '-')
    .replace(/\s+/g, ' ')
    .trim() || 'document';
}

export type BucketId =
  | 'avatars'
  | 'menu-images'
  | 'sops'
  | 'training-videos'
  | 'supplier-docs'
  | 'report-exports';

export interface UploadedFile {
  name: string;
  path: string;
  size: number;
  updated_at: string;
  url: string | null;
}

const PUBLIC_BUCKETS: ReadonlySet<BucketId> = new Set(['avatars', 'menu-images']);

export function useFileUpload(bucket: BucketId, venueId: string | null) {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);

  const list = useCallback(async () => {
    if (!venueId) {
      setFiles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error: err } = await supabase.storage
      .from(bucket)
      .list(venueId, { limit: 100, sortBy: { column: 'updated_at', order: 'desc' } });
    if (err) {
      setError(err.message);
      setFiles([]);
    } else {
      const mapped: UploadedFile[] = (data ?? []).map((f) => {
        const path = `${venueId}/${f.name}`;
        let url: string | null = null;
        if (PUBLIC_BUCKETS.has(bucket)) {
          const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
          url = pub.publicUrl;
        }
        return {
          name: f.name,
          path,
          size: (f.metadata?.size as number | undefined) ?? 0,
          updated_at: f.updated_at ?? f.created_at ?? '',
          url,
        };
      });
      setFiles(mapped);
      setError(null);
    }
    setLoading(false);
  }, [bucket, venueId]);

  useEffect(() => {
    void list();
  }, [list]);

  const upload = useCallback(
    async (file: File) => {
      if (!venueId) return { error: 'no venue', path: null as string | null };
      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
      if (file.size > MAX_FILE_SIZE) return { error: `File too large. Maximum size is 10MB.`, path: null as string | null };
      const safeName = storageSafeName(file.name);
      const path = `${venueId}/${Date.now()}-${safeName}`;
      setUploading(true);
      setProgress(0);
      const { error: err } = await supabase.storage.from(bucket).upload(path, file, {
        contentType: file.type || undefined,
        cacheControl: '3600',
        upsert: false,
      });
      setUploading(false);
      setProgress(0);
      if (err) {
        log.warn('upload failed', { code: err.message, bucket });
        return { error: err.message, path: null as string | null };
      }
      await list();
  
      return { error: null, path: path as string | null };
    },
    [bucket, venueId, list],
  );
  
  const rename = useCallback(
    async (path: string, newName: string) => {
      if (!venueId) return { error: 'no venue' };
      // Preserve original extension
      const originalExt = path.split('.').pop() ?? '';
      const nameWithoutExt = newName.replace(/\.[^.]+$/, '');
      const finalName = originalExt ? `${nameWithoutExt}.${originalExt}` : newName;
      const safeName = storageSafeName(finalName);
      const newPath = `${venueId}/${safeName}`;
      const { error: err } = await supabase.storage.from(bucket).move(path, newPath);
      if (err) return { error: err.message };
      // Keep the document's type attached to its new name.
      await supabase.from('document_meta').update({ path: newPath, updated_at: new Date().toISOString() }).eq('bucket', bucket).eq('path', path);
      // Keep the AI's saved text with the file (best-effort; the table may not exist yet).
      await supabase.from('document_text').update({ path: newPath }).eq('bucket', bucket).eq('path', path);
      await list();
      return { error: null };
    },
    [bucket, venueId, list],
  );

  const remove = useCallback(
    async (path: string) => {
      const { error: err } = await supabase.storage.from(bucket).remove([path]);
      if (err) return { error: err.message };
      await supabase.from('document_meta').delete().eq('bucket', bucket).eq('path', path);
      await supabase.from('document_text').delete().eq('bucket', bucket).eq('path', path);
      await list();
      return { error: null };
    },
    [bucket, list],
  );

  const getSignedUrl = useCallback(
    async (path: string) => {
      if (PUBLIC_BUCKETS.has(bucket)) {
        const { data } = supabase.storage.from(bucket).getPublicUrl(path);
        return { url: data.publicUrl, error: null };
      }
      const { data, error: err } = await supabase.storage.from(bucket).createSignedUrl(path, 300);
      if (err) return { url: null, error: err.message };
      return { url: data.signedUrl, error: null };
    },
    [bucket],
  );

  return { files, loading, uploading, progress, error, upload, remove, rename, getSignedUrl, reload: list };
}
